import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {CurrencyService, InvoiceOrderService, PartnerService} from '@app/core';
import {ActivatedRoute} from '@angular/router';
import {
  AnalyticOperation,
  Currency,
  CurrencyExchange,
  getAllOrderSignatureSteps,
  getServiceSuppliersResolved,
  GetParams,
  InvoiceOrder,
  OrderSignatureStep,
  Partner,
  Project,
  PurchaseOrder,
  Service,
  ServiceOrder,
  SharedModule
} from '@app/shared';
import {DecimalPipe} from '@angular/common';
import {NzCellAlignDirective, NzTableComponent} from 'ng-zorro-antd/table';
import {NzDescriptionsComponent, NzDescriptionsItemComponent} from 'ng-zorro-antd/descriptions';
import {NzColDirective, NzRowDirective} from 'ng-zorro-antd/grid';
import {environment} from '../../../../../environments/environment';
import {forkJoin} from 'rxjs';
import {MaestrosUtilsService} from '../../../maestros/services/maestros-utils.service';

/**
 * Grupo del resumen de Operaciones Analíticas.
 * Misma descripción + mismo precio unitario + misma moneda = mismo grupo: un servicio con precios
 * distintos se refleja en filas separadas y la columna Valor unitario muestra el precio real de la
 * operación, no un promedio que no existe en ninguna de ellas. La moneda entra en la clave porque
 * una fila del resumen representa un concepto facturable y no puede estar en dos monedas a la vez.
 * El importe NO interviene en la agrupación: la misma descripción al mismo precio y en la misma
 * moneda va en una sola fila aunque las cantidades difieran.
 */
interface GroupedByDescription {
  description: string;
  displayLabel: string;
  /** Precio unitario real de las AO del grupo (todas comparten precio por la clave de agrupación). */
  unitPrice: number;
  /** Moneda de las AO del grupo; entra en la clave, así que todas comparten la misma. */
  currencyId: number | null;
  /** ISO de la moneda del grupo, para desempatar el orden de las filas. */
  currencyIsoCode: string;
  /** Textos de Detalle distintos de las AO del grupo, en orden de aparición y sin repetir. */
  details: string[];
  /** Suma de Cantidad de las AO del grupo: es lo que muestra la columna Detalle del resumen. */
  totalQuantity: number;
  totalAmountUSD: number;
  totalTaxUSD: number;
  totalAmountOrderCurrency: number;
  totalTaxOrderCurrency: number;
  totalDinamicData?: Record<string, number>;
  displayIndex?: number;
}

interface SummaryChunk {
  rows: GroupedByDescription[];
  includeTotals: boolean;
}

interface FlattenedAnalyticOperation {
  operation: AnalyticOperation;
  service: Service;
  serviceOrder: ServiceOrder;
  amountInOrderCurrency: number;
}

interface OperationsChunk {
  rows: FlattenedAnalyticOperation[];
  includeTotalsSummary: boolean;
}

interface PdfPage {
  summary?: SummaryChunk;
  operations?: OperationsChunk;
  showStaticTop: boolean;
  showSignature: boolean;
}

interface LayoutMetrics {
  contentHeight: number;
  staticSectionHeight: number;
  summaryHeaderHeight: number;
  summaryRowHeights: number[];
  summaryRowFallbackHeight: number;
  summaryTotalsHeight: number;
  operationsHeaderWithTotalsHeight: number;
  operationsHeaderWithoutTotalsHeight: number;
  operationsRowHeights: number[];
  operationsRowFallbackHeight: number;
  signatureHeight: number;
  footerHeight: number;
  contentGap: number;
}

interface InternalPageState {
  page: PdfPage;
  remainingHeight: number;
  blocksInContent: number;
  summaryState?: {
    chunk: SummaryChunk;
  };
  operationsState?: {
    chunk: OperationsChunk;
    includeTotalsSummary: boolean;
  };
}

@Component({
  selector: 'app-pdf',
  templateUrl: './pdf.component.html',
  imports: [
    DecimalPipe,
    NzTableComponent,
    NzDescriptionsComponent,
    NzDescriptionsItemComponent,
    SharedModule,
    NzCellAlignDirective,
    NzRowDirective,
    NzColDirective,
  ],
  styleUrls: ['./pdf.component.less']
})
export class PdfComponent implements OnInit, AfterViewChecked, AfterViewInit {

  orderId: string = '';
  clientId: string = '';
  currencyId: string = '';
  companyName: string | null = null;
  ivaText: string = 'I.V.A.';
  order!: InvoiceOrder | PurchaseOrder;
  client!: Partner;
  isInvoiceOrder: boolean = true;

  groupedServices: GroupedByDescription[] = [];

  isLoading = true;
  errorMessage: string | null = null;

  pages: PdfPage[] = [];

  private layoutMetrics: LayoutMetrics | null = null;
  private pendingPagination = false;
  private paginationRetryCount = 0;
  private operationsRows: FlattenedAnalyticOperation[] = [];

  totalRow: {
    id: string;
    totalIva: number;
    /** Precio unitario medio ponderado por cantidad (columna Valor unitario en totales). */
    averageUnitPrice: number;
    /** Suma de Cantidad de todas las AO (columna Detalle en totales). */
    detail: number;
    usd: number;
    amount: number;
    totalTaxOrderCurrency: number;
    totalDinamicData: Record<string, number>;
  } = {
    id: 'Totales',
    totalIva: 0,
    averageUnitPrice: 0,
    detail: 0,
    usd: 0,
    amount: 0,
    totalTaxOrderCurrency: 0,
    totalDinamicData: {}
  };

  isPdfReady = false;
  logoLoaded = false;
  accessToken: any;
  respuestaPeticion: any;
  respuestaPeticionPartner: any;
  protected readonly environment = environment;
  private viewChecked = false;
  private _pageContentRefs: QueryList<ElementRef<HTMLElement>> | null = null;
  private _summaryTableRefs: QueryList<ElementRef<HTMLElement>> | null = null;
  private _summaryInlineTableRef: ElementRef<HTMLElement> | null = null;
  private _staticContentAreaRef: ElementRef<HTMLElement> | null = null;
  private _summarySectionContainerRef: ElementRef<HTMLElement> | null = null;
  private _operationsSectionContainerRef: ElementRef<HTMLElement> | null = null;
  private _operationsSummaryRowRef: ElementRef<HTMLElement> | null = null;
  private _signatureSectionRef: ElementRef<HTMLElement> | null = null;
  private _footerRef: ElementRef<HTMLElement> | null = null;
  private readonly defaultRowHeight = 32;
  private orderDataReady = false;
  // La petición de la orden ha terminado, con datos o con error
  private orderSettled = false;
  private clientDataReady = false;

  currentCurrency: Currency | undefined;

  _dinamicCurrencyColumns = new Map<number, { id: number, name: string }>();
  get dinamicCurrencyColumns(): { id: number, name: string }[] {
    return Array.from(this._dinamicCurrencyColumns.values());
  }

  constructor(private invoiceOrderService: InvoiceOrderService,
              private route: ActivatedRoute,
              private partnerService: PartnerService,
              private currencyService: CurrencyService,
              private cdr: ChangeDetectorRef,
              private maestrosUtilsService: MaestrosUtilsService,
  ) {
  }

  @ViewChildren('pageContent', { read: ElementRef })
  set pageContentElements(refs: QueryList<ElementRef<HTMLElement>>) {
    this._pageContentRefs = refs;
  }

  @ViewChildren('summaryTable', { read: ElementRef })
  set summaryTableRefs(refs: QueryList<ElementRef<HTMLElement>>) {
    this._summaryTableRefs = refs;
    this.checkTables();
  }

  @ViewChild('summaryInlineTable', { read: ElementRef })
  set summaryInlineTable(ref: ElementRef<HTMLElement>) {
    this._summaryInlineTableRef = ref;
    this.checkTables();
  }

  @ViewChild('staticContentArea', { read: ElementRef })
  set staticContentArea(ref: ElementRef<HTMLElement>) {
    this._staticContentAreaRef = ref;
  }

  @ViewChild('summarySectionContainer', { read: ElementRef })
  set summarySectionContainer(ref: ElementRef<HTMLElement>) {
    this._summarySectionContainerRef = ref;
  }

  @ViewChild('operationsSectionContainer', { read: ElementRef })
  set operationsSectionContainer(ref: ElementRef<HTMLElement>) {
    this._operationsSectionContainerRef = ref;
  }

  @ViewChild('operationsSummaryRow', { read: ElementRef })
  set operationsSummaryRow(ref: ElementRef<HTMLElement>) {
    this._operationsSummaryRowRef = ref;
  }

  @ViewChild('signatureSection', { read: ElementRef })
  set signatureSection(ref: ElementRef<HTMLElement>) {
    this._signatureSectionRef = ref;
  }

  @ViewChild('pageFooter', { read: ElementRef })
  set footer(ref: ElementRef<HTMLElement>) {
    this._footerRef = ref;
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.isInvoiceOrder = this.route.snapshot.url.some(segment => segment.path === 'estado-pago');
      this.orderId = params['id'];
      this.clientId = params['clientId']
      this.currencyId = params['currencyId']
      // Obtener el nombre de la compañía de los parámetros de ruta o query params
      this.companyName = params['companyName'] || this.route.snapshot.queryParams['companyName'] || null;

      // Obtener el texto del IVA según el nombre de la compañía
      this.ivaText = this.maestrosUtilsService.getIvaTextByCompanyName(this.companyName);

      // Obtener el valor de la cookie 'jwt'
      const getCookie = (name: any) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop()!.split(';').shift();
        else return null;
      };

      // Obtener el valor de la cookie 'jwt'
      this.accessToken = getCookie('jwt');

      this.loadOrder();
    });
  }

  ngAfterViewChecked(): void {
    // orderSettled: la carga del cliente también libera isLoading, y la orden puede
    // seguir esperando su tipo de cambio guardado
    if (!this.isLoading && !this.viewChecked && this.orderSettled) {
      if (this.logoLoaded && this.client) {
        this.markAsReady();
      }
    }
  }

  onLogoLoaded(): void {
    this.logoLoaded = true;

    if (!this.isLoading && !this.viewChecked && this.orderSettled && this.client) {
      this.markAsReady();
    }
  }

  isInvoice(order: InvoiceOrder | PurchaseOrder): order is InvoiceOrder {
    return this.isInvoiceOrder;
  }

  isPurchase(order: InvoiceOrder | PurchaseOrder): order is PurchaseOrder {
    return !this.isInvoiceOrder;
  }

  /**
   * Ruta de firmas del documento (SOU-481). Las cuatro columnas se imprimen SIEMPRE,
   * incluidas las pendientes, pero ordenadas por la fecha real de cada paso: primero
   * las firmadas en orden cronológico y luego las que faltan, en el orden nuevo.
   *
   * Así un EP nuevo se imprime Emitido · Revisado · Aprobado · Enviado, y uno anterior
   * al cambio conserva su recorrido original —Enviado antes de Aprobado— en vez de
   * reescribirse con un orden que no fue el suyo.
   */
  get signatureSteps(): OrderSignatureStep[] {
    return getAllOrderSignatureSteps(this.order);
  }

  /** Proyecto para la cabecera del PDF: order.project o el primero disponible en analyticOperations[].serviceOrder.service.project. */
  get pdfProject(): Project | undefined {
    if (this.order?.project) return this.order.project;
    const ao = this.order?.analyticOperations?.find(a => a.serviceOrder?.service?.project);
    return ao?.serviceOrder?.service?.project;
  }

  /**
   * Agrupa AnalyticOperations por descripción + precio unitario (mismos dos = mismo grupo),
   * de modo que un servicio con precios distintos aparece en filas separadas. El importe no
   * interviene: distintas cantidades al mismo precio se suman en una sola fila.
   * La columna Detalle es esa suma de cantidades, no el número de AO agrupadas, para que
   * Valor unitario × Detalle dé el importe de la fila (ticket 2452).
   * Orden alfabético por descripción y precio descendente; descripción vacía al final con label "Sin tipo".
   */
  buildSummaryGroups(): void {
    const orderCurrencyExchange = this.currentCurrency?.currentCurrencyExchange?.baseRate || 1;
    const groupMap = new Map<string, {
      description: string;
      unitPrice: number;
      currencyId: number | null;
      currencyIsoCode: string;
      details: string[];
      detailsSeen: Set<string>;
      totalQuantity: number;
      totalAmountUSD: number;
      totalTaxUSD: number;
      totalAmountOrderCurrency: number;
      totalTaxOrderCurrency: number;
      totalDinamicData: Record<string, number>;
    }>();

    this.order?.analyticOperations?.forEach(operation => {
      const serviceOrder = operation.serviceOrder;
      if (!serviceOrder || !serviceOrder.service) return;
      const service = serviceOrder.service;

      if (this.isInvoiceOrder) {
        // PARA INVOICE ORDER: Filtrar por partner seleccionado al emitir
        let partnerId: number | undefined;
        
        // PRIORIDAD 1: Usar partner de pdfPartners (el seleccionado al emitir)
        if (this.order?.pdfPartners && this.order.pdfPartners.length > 0) {
          partnerId = this.order.pdfPartners[0].partnerId;
        } else {
          // PRIORIDAD 2: Fallback al partner del proyecto (legado)
          partnerId = service.project?.partnerId;
        }
        
        if (!partnerId || partnerId.toString() !== this.clientId) return;
      } else {
        let supplierId: number | undefined;
        if (this.order?.pdfPartners && this.order.pdfPartners.length > 0) {
          supplierId = this.order.pdfPartners[0].partnerId;
        } else {
          const { supplierIds } = getServiceSuppliersResolved(service);
          supplierId = supplierIds[0] || service.supplierId;
        }
        if (!supplierId || supplierId.toString() !== this.clientId) return;
      }

      const description = (operation.description ?? '').trim();
      const operationCurrency = operation.currency;
      const operationCurrencyExchange = operationCurrency?.currentCurrencyExchange?.baseRate || 1;
      const amount = operation.amount ?? 0;
      const quantity = operation.quantity ?? 0;
      const unitPrice = operation.unitPrice ?? 0;
      // Un mismo servicio con precios unitarios distintos, o en monedas distintas, va en filas
      // separadas del resumen: dos operaciones en monedas diferentes son dos servicios distintos y
      // cada uno debe llevar su importe en la columna de su moneda.
      // El importe queda fuera de la clave: la misma descripción al mismo precio y moneda se agrupa
      // en una sola fila aunque las cantidades (y por tanto los importes) difieran entre operaciones.
      const currencyId = operationCurrency?.id ?? null;
      const currencyIsoCode = operationCurrency?.isoCode ?? '';
      const key = `${description}|${unitPrice}|${currencyId ?? ''}`;

      let vatPercent = 0;
      if (operation.applyVat !== undefined && operation.applyVat !== null) {
        // IVA definido a nivel de operación analítica
        vatPercent = operation.applyVat ? (operation.vatPercentage ?? 0) : 0;
      } else {
        // Fallback a ServicePricing (registros antiguos sin los nuevos campos)
        if (this.isInvoiceOrder) {
          vatPercent = service.servicePricing?.unitSaleVat ?? service.servicePricing?.totalSaleVat ?? 0;
        } else {
          vatPercent = service.servicePricing?.unitPurchaseVat ?? service.servicePricing?.totalPurchaseVat ?? 0;
        }
      }

      const taxInOperationCurrency = amount * (vatPercent / 100);
      let amountUSD = 0;
      let taxUSD = 0;
      let amountInOrderCurrency = amount;
      let taxInOrderCurrency = taxInOperationCurrency;

      if (operationCurrency?.id === this.currentCurrency?.id) {
        amountUSD = amount / orderCurrencyExchange;
        taxUSD = taxInOperationCurrency / orderCurrencyExchange;
        taxInOrderCurrency = taxInOperationCurrency;
      } else {
        amountUSD = amount / operationCurrencyExchange;
        taxUSD = taxInOperationCurrency / operationCurrencyExchange;
        amountInOrderCurrency = amount * (orderCurrencyExchange / operationCurrencyExchange);
        taxInOrderCurrency = taxInOperationCurrency * (orderCurrencyExchange / operationCurrencyExchange);
      }

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          description,
          unitPrice,
          currencyId,
          currencyIsoCode,
          details: [],
          detailsSeen: new Set<string>(),
          totalQuantity: 0,
          totalAmountUSD: 0,
          totalTaxUSD: 0,
          totalAmountOrderCurrency: 0,
          totalTaxOrderCurrency: 0,
          totalDinamicData: {}
        });
      }
      const g = groupMap.get(key)!;

      // Detalle de la AO: se acumulan los textos distintos del grupo, en orden de aparición.
      // Se comparan sin distinguir mayúsculas ni espacios sobrantes, pero se conserva el original.
      const detail = (operation.details ?? '').trim();
      if (detail) {
        const detailKey = detail.toLocaleLowerCase();
        if (!g.detailsSeen.has(detailKey)) {
          g.detailsSeen.add(detailKey);
          g.details.push(detail);
        }
      }

      g.totalQuantity += quantity;
      g.totalAmountUSD += amountUSD;
      g.totalTaxUSD += taxUSD;
      g.totalAmountOrderCurrency += amountInOrderCurrency;
      g.totalTaxOrderCurrency += taxInOrderCurrency;

      if (operation.currency) {
        this._dinamicCurrencyColumns.set(operation.currency.id, { id: operation.currency.id, name: operation.currency.isoCode });
        const cKey = `currency_${operation.currency.id}`;
        g.totalDinamicData[cKey] = (g.totalDinamicData[cKey] || 0) + amount;
      }
    });

    const emptyKey = '';
    const groups = Array.from(groupMap.values());
    // Alfabético por descripción (la vacía al final), dentro de una misma descripción de mayor a
    // menor precio unitario y, a igual precio, alfabético por ISO de moneda. Los tres campos son
    // la clave del grupo, así que el criterio es un orden total: no hacen falta más desempates.
    const sorted = groups.sort((a, b) => {
      if (a.description === emptyKey && b.description !== emptyKey) return 1;
      if (b.description === emptyKey && a.description !== emptyKey) return -1;
      const byDescription = a.description.localeCompare(b.description);
      if (byDescription !== 0) return byDescription;
      const byUnitPrice = b.unitPrice - a.unitPrice;
      if (byUnitPrice !== 0) return byUnitPrice;
      return a.currencyIsoCode.localeCompare(b.currencyIsoCode);
    });

    this.groupedServices = sorted.map((g, index) => ({
      description: g.description,
      displayLabel: g.description || 'Sin tipo',
      unitPrice: g.unitPrice,
      currencyId: g.currencyId,
      currencyIsoCode: g.currencyIsoCode,
      details: g.details,
      totalQuantity: g.totalQuantity,
      totalAmountUSD: g.totalAmountUSD,
      totalTaxUSD: g.totalTaxUSD,
      totalAmountOrderCurrency: g.totalAmountOrderCurrency,
      totalTaxOrderCurrency: g.totalTaxOrderCurrency,
      totalDinamicData: g.totalDinamicData,
      displayIndex: index + 1
    }));

    this.totalRow.totalIva = this.groupedServices.reduce((sum, g) => sum + g.totalTaxUSD, 0);
    // Detalle de Totales = suma de cantidades, y el precio medio se pondera por esa misma
    // cantidad para que Valor unitario × Detalle cuadre también en la línea de Totales.
    this.totalRow.detail = this.groupedServices.reduce((sum, g) => sum + g.totalQuantity, 0);
    const totalDetail = this.totalRow.detail;
    this.totalRow.averageUnitPrice = totalDetail !== 0
      ? this.groupedServices.reduce((sum, g) => sum + g.unitPrice * g.totalQuantity, 0) / totalDetail
      : 0;
    this.totalRow.usd = this.groupedServices.reduce((sum, g) => sum + g.totalAmountUSD, 0);
    this.totalRow.amount = this.groupedServices.reduce((sum, g) => sum + g.totalAmountOrderCurrency, 0);
    this.totalRow.totalTaxOrderCurrency = this.groupedServices.reduce((sum, g) => sum + g.totalTaxOrderCurrency, 0);

    const totalDinamicData: Record<string, number> = {};
    this.groupedServices.forEach(group => {
      Object.keys(group.totalDinamicData ?? {}).forEach(k => {
        totalDinamicData[k] = (totalDinamicData[k] || 0) + (group.totalDinamicData![k] || 0);
      });
    });
    this.totalRow.totalDinamicData = totalDinamicData;
  }

  /**
   * Porcentaje efectivo de impuesto de una fila del resumen o de la línea de Totales.
   * Devuelve null cuando el porcentaje no es representable, y la plantilla pinta '-'.
   *
   * El signo NO importa: con una base negativa (nota de crédito o ajuste) el impuesto también lo
   * es y el cociente sale igual de positivo, así que una operación al 100% se imprime como 100%
   * y no como el '-%' de antes. Lo que sí invalida el cálculo es una base nula: dividir por ella
   * daría infinito, y devolver 0 haría que el documento afirmara que no hay impuesto cuando sí
   * lo hay. El umbral de medio céntimo cubre el caso de unos Totales que mezclan importes de
   * signo opuesto y suman una base casi nula, que dispararía el porcentaje a miles.
   */
  getVatPercent(tax: number | null | undefined, base: number | null | undefined): number | null {
    if (tax === null || tax === undefined || base === null || base === undefined) return null;
    if (Math.abs(base) < 0.005) return null;
    return tax / base * 100;
  }

  loadOrder(): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.orderDataReady = false;
    this.orderSettled = false;
    this.clientDataReady = false;

    const getParams: GetParams = {
      startRow: 0,
      endRow: 1,
      sortDirection: 'asc',
      sortProperty: 'id',
      filterProperty: 'id',
      filterValue: this.orderId
    };

    const currencyParams: GetParams = {
      startRow: 0,
      endRow: 1,
      sortDirection: 'asc',
      sortProperty: 'id',
      filterProperty: 'id',
      filterValue: this.currencyId
    };

    // Determinar la petición de orden según el tipo
    const orderRequest = this.isInvoiceOrder
      ? this.invoiceOrderService.getInvoiceOrder(getParams, this.accessToken)
      : this.invoiceOrderService.getPurchaseOrder(getParams, this.accessToken);

    // Configurar las peticiones paralelas
    const requests = {
      currency: this.currencyService.getCurrency(currencyParams, this.accessToken),
      order: orderRequest
    };

    forkJoin(requests).subscribe({
      next: ({ currency, order }) => {

        if (order.data?.rows?.length > 0) {
          this.order = this.isInvoiceOrder
            ? (order.data.rows[0] as InvoiceOrder)
            : (order.data.rows[0] as PurchaseOrder);

          // Usar taxType del orden si está disponible; fallback a companyName para órdenes legacy
          this.ivaText = this.order.taxType
            || this.maestrosUtilsService.getIvaTextByCompanyName(this.companyName);

          if (currency.data?.rows?.length > 0) {
            this.currentCurrency = currency.data.rows[0];
          } else {
            this.currentCurrency = this.order.currency;
          }

          // isLoading se libera dentro, cuando ya está resuelto el tipo de cambio:
          // si no, el PDF podría marcarse como listo con los importes sin calcular
          this.applyStoredCurrencyExchange();
        } else {
          // Manejar caso de orden vacía
          this.errorMessage = this.isInvoiceOrder
            ? 'No se encontró la orden de factura.'
            : 'No se encontró El estado de pago.';
          this.orderSettled = true;
          this.isLoading = false;
        }
      },
      error: (err) => {
        // Manejar caso de orden vacía
        this.errorMessage = this.isInvoiceOrder
          ? 'Error al obtener la orden de factura.'
          : 'Error al obtener el estado de pago.';
        this.orderSettled = true;
        this.isLoading = false;
        console.error('Error loading data', err);
      }
    });


    getParams.filterValue = this.clientId
    this.partnerService.getPartner(getParams, this.accessToken).subscribe({
      next: (response) => {
        this.respuestaPeticionPartner = response
        if (response.data && response.data.rows.length > 0) {
          this.client = response.data.rows[0];
        } else {
          this.errorMessage = 'No se encontró el cliente.';
        }
        this.isLoading = false;
        this.clientDataReady = true;
        this.tryInitializePagination();
      },
      error: (err) => {
        this.respuestaPeticionPartner = err
        this.errorMessage = 'Error al cargar los datos del cliente.';
        this.isLoading = false;
        console.error('Error loading client', err);
        this.clientDataReady = true;
        this.tryInitializePagination();
      }
    });
  }

  ngAfterViewInit() {
    this.checkTables();
  }

  checkTables() {
    const summaryTableRef = this._summaryTableRefs?.last;
    if (summaryTableRef && this._summaryInlineTableRef) {
      setTimeout(() => this.syncColumnWidths(summaryTableRef.nativeElement, this._summaryInlineTableRef!.nativeElement), 0);
    }
  }

  private syncColumnWidths(mainTableEl: HTMLElement, inlineTableEl: HTMLElement) {
    if (!mainTableEl || !inlineTableEl) {
      return;
    }

    const mainHeaders = mainTableEl.querySelectorAll('thead tr:first-child th') as NodeListOf<HTMLElement>;
    const inlineHeaders = inlineTableEl.querySelectorAll('tr:first-child th, tr:first-child td') as NodeListOf<HTMLElement>;
    const inlineRows = inlineTableEl.querySelectorAll('tbody tr') as NodeListOf<HTMLTableRowElement>;

    Array.from(mainHeaders).forEach((header, index) => {
      if (inlineHeaders[index]) {
        const width = `${header.offsetWidth}px`;

        inlineHeaders[index].style.width = width;
        inlineHeaders[index].style.minWidth = width;

        Array.from(inlineRows).forEach(row => {
          const cell = row.cells?.[index] as HTMLElement | undefined;
          if (cell) {
            cell.style.width = width;
            cell.style.minWidth = width;
          }
        });
      }
    });
  }

  private markAsReady(): void {
    setTimeout(() => {
      this.isPdfReady = true;
      this.viewChecked = true;
    }, 500);
  }

  /**
   * El tipo de cambio impreso es el fijado al emitir la orden (`currencyExchangeId`), no el
   * vigente: el PDF se rehace en cada paso de la ruta de firmas y con el vigente cambiaría
   * el tipo de cambio de un documento ya emitido (SOU-558). Solo aplica cuando el PDF va en
   * la moneda de la orden; sin tipo guardado (borradores, órdenes antiguas) se usa el vigente.
   */
  private applyStoredCurrencyExchange(): void {
    const storedExchangeId = this.order?.currencyExchangeId;
    const currency = this.currentCurrency;

    if (!storedExchangeId || !currency || currency.id !== this.order?.currencyId
      || currency.currentCurrencyExchange?.id === storedExchangeId) {
      this.finishOrderLoad();
      return;
    }

    const params: GetParams = {
      startRow: 0,
      endRow: 1,
      sortDirection: 'asc',
      sortProperty: 'id',
      filterProperty: 'id',
      filterValue: String(storedExchangeId)
    };

    this.currencyService.getCurrencyExchange(params, this.accessToken).subscribe({
      next: (response) => {
        const storedExchange: CurrencyExchange | undefined = response?.data?.rows?.[0];
        if (storedExchange?.baseRate) {
          // Copia: no alterar el objeto de moneda que pueda compartir otra vista
          this.currentCurrency = {...currency, currentCurrencyExchange: storedExchange};
        } else {
          console.warn(`Tipo de cambio ${storedExchangeId} no encontrado; se usa el vigente`);
        }
        this.finishOrderLoad();
      },
      error: (err) => {
        console.warn(`No se pudo cargar el tipo de cambio ${storedExchangeId}; se usa el vigente`, err);
        this.finishOrderLoad();
      }
    });
  }

  private finishOrderLoad(): void {
    this.processData();
    this.orderSettled = true;
    this.isLoading = false;
  }

  ////////////Paginado//////////

  private processData() {
    if (!this.order) {
      return;
    }

    if (this.order.analyticOperations) {
      this.buildSummaryGroups();
    }

    this.operationsRows = this.buildOperationRows();
    this.orderDataReady = true;
    this.tryInitializePagination();
  }

  private buildOperationRows(): FlattenedAnalyticOperation[] {
    const rows: FlattenedAnalyticOperation[] = [];
    const orderCurrencyExchange = this.currentCurrency?.currentCurrencyExchange?.baseRate || 1;

    this.order?.analyticOperations?.forEach(operation => {
      const serviceOrder = operation.serviceOrder;
      if (!serviceOrder || !serviceOrder.service) {
        return;
      }

      const service = serviceOrder.service;

      // Filtrar los datos que pertenecen al Partner
      if (this.isInvoiceOrder) {
        // PARA INVOICE ORDER: Filtrar por partner seleccionado al emitir
        let partnerId: number | undefined;
        
        // PRIORIDAD 1: Usar partner de pdfPartners (el seleccionado al emitir)
        if (this.order?.pdfPartners && this.order.pdfPartners.length > 0) {
          partnerId = this.order.pdfPartners[0].partnerId;
        } else {
          // PRIORIDAD 2: Fallback al partner del proyecto (legado)
          partnerId = service.project?.partnerId;
        }
        
        if (!partnerId || partnerId.toString() !== this.clientId) {
          return;
        }
      } else {
        // Para PurchaseOrder, usar el supplier del InvoiceOrder (pdfPartners[0].partnerId) si existe
        // Si no, usar el supplier del Service (compatibilidad)
        let supplierId: number | undefined;
        if (this.order?.pdfPartners && this.order.pdfPartners.length > 0) {
          supplierId = this.order.pdfPartners[0].partnerId;
        } else {
          const { supplierIds } = getServiceSuppliersResolved(service);
          supplierId = supplierIds[0] || service.supplierId;
        }

        if (!supplierId || supplierId.toString() !== this.clientId) {
          return;
        }
      }
      const operationCurrency = operation.currency;
      const operationCurrencyExchange = operationCurrency?.currentCurrencyExchange?.baseRate || 1;

      let amountInOrderCurrency = operation.amount || 0;
      if (operationCurrency?.id !== this.currentCurrency?.id) {
        amountInOrderCurrency = (operation.amount || 0) * (orderCurrencyExchange / operationCurrencyExchange);
      }

      rows.push({
        operation,
        service,
        serviceOrder,
        amountInOrderCurrency
      });
    });

    return rows;
  }

  private initializePagination(): void {
    this.pendingPagination = true;
    this.paginationRetryCount = 0;

    const initialPage: PdfPage = {
      showStaticTop: true,
      showSignature: true
    };

    if (this.groupedServices.length) {
      initialPage.summary = {
        rows: [...this.groupedServices],
        includeTotals: true
      };
    }

    if (this.operationsRows.length) {
      initialPage.operations = {
        rows: [...this.operationsRows],
        includeTotalsSummary: true
      };
    }

    this.pages = [initialPage];

    setTimeout(() => this.applyDynamicPagination());
  }

  private tryInitializePagination(): void {
    if (!this.orderDataReady) {
      return;
    }

    if (!this.clientDataReady) {
      return;
    }

    this.initializePagination();
  }

  private applyDynamicPagination(): void {
    if (!this.pendingPagination) {
      return;
    }

    const metrics = this.collectLayoutMetrics();

    if (!metrics) {
      if (this.paginationRetryCount < 5) {
        this.paginationRetryCount += 1;
        setTimeout(() => this.applyDynamicPagination(), 50);
      } else {
        this.pendingPagination = false;
      }
      return;
    }

    this.layoutMetrics = metrics;
    const pages = this.buildPaginatedPages(metrics);
    this.pages = pages;
    this.pendingPagination = false;
    this.cdr.detectChanges();
  }

  private collectLayoutMetrics(): LayoutMetrics | null {
    const contentEl = this._pageContentRefs?.first?.nativeElement;
    if (!contentEl) {
      return null;
    }

    const staticContentEl = this._staticContentAreaRef?.nativeElement;
    const summaryContainerEl = this._summarySectionContainerRef?.nativeElement;
    const summaryTableEl = this._summaryTableRefs?.first?.nativeElement;
    const summaryTotalsEl = this._summaryInlineTableRef?.nativeElement;
    const operationsContainerEl = this._operationsSectionContainerRef?.nativeElement;
    const operationsSummaryRowEl = this._operationsSummaryRowRef?.nativeElement;
    const signatureEl = this._signatureSectionRef?.nativeElement;
    const footerEl = this._footerRef?.nativeElement;

    if (!signatureEl || !footerEl) {
      return null;
    }

    const contentRect = contentEl.getBoundingClientRect();
    const contentGap = typeof window !== 'undefined'
      ? parseFloat(window.getComputedStyle(contentEl).rowGap || '0') || 0
      : 24;

    const hasSummary = this.groupedServices.length > 0;
    const hasOperations = this.operationsRows.length > 0;

    let staticSectionHeight = 0;
    if (staticContentEl) {
      staticSectionHeight = this.measureBlockHeight(staticContentEl);
    }

    let summaryRowHeights: number[] = [];
    let summaryTotalsHeight = 0;
    let summaryHeaderHeight = 0;

    if (hasSummary) {
      if (!summaryContainerEl || !summaryTableEl || !summaryTotalsEl) {
        return null;
      }

      const summaryRows = summaryTableEl.querySelectorAll('tbody tr');
      summaryRowHeights = Array.from(summaryRows).map(row => this.measureRowHeight(row as HTMLElement));
      if (!summaryRowHeights.length || summaryRowHeights.some(height => height < 1)) {
        return null;
      }
      summaryTotalsHeight = summaryTotalsEl ? this.measureBlockHeight(summaryTotalsEl) : 0;
      const summaryContainerHeight = this.measureBlockHeight(summaryContainerEl);
      const totalSummaryRowsHeight = summaryRowHeights.reduce((acc, height) => acc + height, 0);
      summaryHeaderHeight = Math.max(0, summaryContainerHeight - totalSummaryRowsHeight - summaryTotalsHeight);
    }

    let operationsRowHeights: number[] = [];
    let operationsHeaderWithTotalsHeight = 0;
    let operationsHeaderWithoutTotalsHeight = 0;

    if (hasOperations) {
      if (!operationsContainerEl) {
        return null;
      }

      const operationsTableEl = operationsContainerEl.querySelector('table');
      const operationsRows = operationsTableEl?.querySelectorAll('tbody tr');
      if (operationsTableEl && operationsRows && operationsRows.length > 0) {
        operationsRowHeights = Array.from(operationsRows).map(row => this.measureRowHeight(row as HTMLElement));
        if (!operationsRowHeights.length || operationsRowHeights.some(height => height < 1)) {
          return null;
        }
        const totalOperationsRowsHeight = operationsRowHeights.reduce((acc, height) => acc + height, 0);
        const operationsContainerHeight = this.measureBlockHeight(operationsContainerEl);
        operationsHeaderWithTotalsHeight = Math.max(0, operationsContainerHeight - totalOperationsRowsHeight);
        const operationsSummaryHeight = this.measureBlockHeight(operationsSummaryRowEl ?? null);
        operationsHeaderWithoutTotalsHeight = Math.max(0, operationsHeaderWithTotalsHeight - operationsSummaryHeight);
      }
    }

    const signatureHeight = signatureEl.getBoundingClientRect().height;
    const footerHeight = footerEl.getBoundingClientRect().height;

    return {
      contentHeight: contentRect.height,
      staticSectionHeight,
      summaryHeaderHeight: summaryHeaderHeight,
      summaryRowHeights,
      summaryRowFallbackHeight: summaryRowHeights.find(height => height > 0) ?? this.defaultRowHeight,
      summaryTotalsHeight: summaryTotalsHeight,
      operationsHeaderWithTotalsHeight,
      operationsHeaderWithoutTotalsHeight,
      operationsRowHeights,
      operationsRowFallbackHeight: operationsRowHeights.find(height => height > 0) ?? this.defaultRowHeight,
      signatureHeight,
      footerHeight,
      contentGap
    };
  }

  private buildPaginatedPages(metrics: LayoutMetrics): PdfPage[] {
    const states: InternalPageState[] = [];
    let currentState = this.startNewPage(states, metrics);

    const summaryRowHeights = metrics.summaryRowHeights;
    const summaryFallbackHeight = metrics.summaryRowFallbackHeight;
    const operationsRowHeights = metrics.operationsRowHeights;
    const operationsFallbackHeight = metrics.operationsRowFallbackHeight;

    const summaryQueue = this.groupedServices.map((group, index) => ({
      data: group,
      height: summaryRowHeights[index] ?? summaryFallbackHeight
    }));

    const operationsQueue = this.operationsRows.map((row, index) => ({
      data: row,
      height: operationsRowHeights[index] ?? operationsFallbackHeight
    }));

    const hasSummary = summaryQueue.length > 0;
    const summaryRequiresTotals = hasSummary;

    while (summaryQueue.length > 0) {
      const isLastRow = summaryQueue.length === 1;
      const nextItem = summaryQueue[0];
      const rowHeight = Math.max(nextItem.height, metrics.summaryRowFallbackHeight);
      const requiredSpace = rowHeight + (isLastRow && summaryRequiresTotals ? metrics.summaryTotalsHeight : 0);
      currentState = this.ensureSummaryChunk(currentState, metrics, states, requiredSpace);

      const item = summaryQueue.shift()!;
      currentState.summaryState!.chunk.rows.push(item.data);
      currentState.remainingHeight = Math.max(0, currentState.remainingHeight - rowHeight);
    }

    if (summaryRequiresTotals) {
      currentState = this.placeSummaryTotals(currentState, metrics, states);
    }

    let operationsStarted = false;

    while (operationsQueue.length > 0) {
      const nextItem = operationsQueue[0];
      const rowHeight = Math.max(nextItem.height, metrics.operationsRowFallbackHeight);
      const requiredSpace = rowHeight;
      currentState = this.ensureOperationsChunk(currentState, metrics, states, operationsStarted, requiredSpace);

      const item = operationsQueue.shift()!;
      currentState.operationsState!.chunk.rows.push(item.data);
      currentState.remainingHeight = Math.max(0, currentState.remainingHeight - rowHeight);
      operationsStarted = true;
    }

    if (states.length) {
      const lastState = states[states.length - 1];
      if (metrics.signatureHeight > 0 && lastState.remainingHeight < metrics.signatureHeight) {
        lastState.page.showSignature = false;
        const signatureState = this.startNewPage(states, metrics);
        signatureState.page.showSignature = true;
      }
    }

    const pages = states.map((state, index) => ({
      ...state.page,
      showStaticTop: index === 0,
      showSignature: state.page.showSignature
    }));

    if (!pages.length) {
      pages.push({
        showStaticTop: true,
        showSignature: true
      });
    }

    if (!pages.some(page => page.showSignature)) {
      pages[pages.length - 1].showSignature = true;
    }

    return pages;
  }

  private startNewPage(states: InternalPageState[], metrics: LayoutMetrics): InternalPageState {
    const isFirstPage = states.length === 0;
    const page: PdfPage = {
      showStaticTop: isFirstPage,
      showSignature: false
    };

    let remainingHeight = metrics.contentHeight;
    let blocksInContent = 0;

    if (isFirstPage && metrics.staticSectionHeight > 0) {
      remainingHeight -= metrics.staticSectionHeight;
      blocksInContent = 1;
    }

    if (remainingHeight < 0) {
      remainingHeight = 0;
    }

    const state: InternalPageState = {
      page,
      remainingHeight,
      blocksInContent
    };

    states.push(state);
    return state;
  }

  private ensureSummaryChunk(currentState: InternalPageState, metrics: LayoutMetrics, states: InternalPageState[], requiredSpace: number): InternalPageState {
    let state = currentState;
    let openedEmptyPage = false;

    while (true) {
      const needsGap = !state.summaryState && state.blocksInContent > 0 && metrics.contentGap > 0;
      const gapSpace = needsGap ? metrics.contentGap : 0;
      const headerSpace = !state.summaryState ? metrics.summaryHeaderHeight : 0;

      // Una fila con muchos Detalles puede ser más alta que una página entera. Si ni en una página
      // recién abierta cabe, se coloca igualmente: si no, el bucle abriría páginas vacías sin fin.
      if (state.remainingHeight >= gapSpace + headerSpace + requiredSpace || openedEmptyPage) {
        if (!state.summaryState) {
          if (gapSpace > 0) {
            state.remainingHeight = Math.max(0, state.remainingHeight - gapSpace);
          }

          const chunk: SummaryChunk = {
            rows: [],
            includeTotals: false
          };

          state.page.summary = chunk;
          state.summaryState = { chunk };
          state.remainingHeight = Math.max(0, state.remainingHeight - metrics.summaryHeaderHeight);
          state.blocksInContent += 1;
        }
        return state;
      }

      state = this.startNewPage(states, metrics);
      openedEmptyPage = true;
    }
  }

  private placeSummaryTotals(currentState: InternalPageState, metrics: LayoutMetrics, states: InternalPageState[]): InternalPageState {
    let state = currentState;
    let openedEmptyPage = false;

    while (true) {
      state = this.ensureSummaryChunk(state, metrics, states, metrics.summaryTotalsHeight);

      if (state.remainingHeight >= metrics.summaryTotalsHeight || openedEmptyPage) {
        state.summaryState!.chunk.includeTotals = true;
        state.remainingHeight = Math.max(0, state.remainingHeight - metrics.summaryTotalsHeight);
        return state;
      }

      state = this.startNewPage(states, metrics);
      openedEmptyPage = true;
    }
  }

  private ensureOperationsChunk(currentState: InternalPageState, metrics: LayoutMetrics, states: InternalPageState[], operationsStarted: boolean, requiredSpace: number): InternalPageState {
    let state = currentState;
    let openedEmptyPage = false;

    while (true) {
      const needsGap = !state.operationsState && state.blocksInContent > 0 && metrics.contentGap > 0;
      const gapSpace = needsGap ? metrics.contentGap : 0;
      const headerHeight = !state.operationsState
        ? (operationsStarted ? metrics.operationsHeaderWithoutTotalsHeight : metrics.operationsHeaderWithTotalsHeight)
        : 0;

      // Mismo blindaje que en el resumen: nunca abrir páginas vacías indefinidamente.
      if (state.remainingHeight >= gapSpace + headerHeight + requiredSpace || openedEmptyPage) {
        if (!state.operationsState) {
          if (gapSpace > 0) {
            state.remainingHeight = Math.max(0, state.remainingHeight - gapSpace);
          }

          const chunk: OperationsChunk = {
            rows: [],
            includeTotalsSummary: !operationsStarted
          };

          state.page.operations = chunk;
          state.operationsState = {
            chunk,
            includeTotalsSummary: !operationsStarted
          };
          state.remainingHeight = Math.max(0, state.remainingHeight - headerHeight);
          state.blocksInContent += 1;
        }

        return state;
      }

      state = this.startNewPage(states, metrics);
      openedEmptyPage = true;
    }
  }

  private measureBlockHeight(element: HTMLElement | null): number {
    if (!element) {
      return 0;
    }

    let height = element.getBoundingClientRect().height;

    if (!height || height < 1) {
      height = element.offsetHeight;
    }

    if ((!height || height < 1) && typeof window !== 'undefined') {
      const styles = window.getComputedStyle(element);
      const marginTop = parseFloat(styles.marginTop) || 0;
      const marginBottom = parseFloat(styles.marginBottom) || 0;
      const computedHeight = parseFloat(styles.height) || 0;
      height = computedHeight + marginTop + marginBottom;
    }

    return height;
  }

  private measureRowHeight(element: HTMLElement | null): number {
    if (!element) {
      return this.defaultRowHeight;
    }

    let height = element.getBoundingClientRect().height;

    if (!height || height < 1) {
      height = element.offsetHeight;
    }

    if ((!height || height < 1) && typeof window !== 'undefined') {
      const styles = window.getComputedStyle(element);
      const marginTop = parseFloat(styles.marginTop) || 0;
      const marginBottom = parseFloat(styles.marginBottom) || 0;
      const computedHeight = parseFloat(styles.height) || 0;
      height = computedHeight + marginTop + marginBottom;
    }

    if (!height || height < 1) {
      height = this.defaultRowHeight;
    }

    return height;
  }

  /**
   * El tipo de cambio solo se imprime si convierte algo: un tipo distinto de 1 y alguna operación
   * del documento en otra moneda. Si no, «1 (01 AGO 2025)» o un tipo que no se aplica a ningún
   * importe solo confunden al cliente (decisión de Dailyn, 16-10, SOU-558).
   */
  get showExchangeRate(): boolean {
    const rate = this.currentCurrency?.currentCurrencyExchange?.baseRate;
    if (!rate || rate === 1) {
      return false;
    }
    // Mismo criterio que buildOperationRows para decidir si una operación se convierte
    return this.operationsRows.some(row => row.operation.currency?.id !== this.currentCurrency?.id);
  }

  /**
   * Fecha del tipo de cambio que se imprime, no la de hoy: el PDF se rehace en cada firma y
   * con la fecha de hoy emparejaría el tipo guardado con un día en que no estaba vigente (SOU-558).
   */
  getExchangeDateFormatted(): string {
    const startDate = this.currentCurrency?.currentCurrencyExchange?.startDate;
    // Solo la parte de fecha: con new Date() sobre un ISO en UTC, la zona de Chile/Perú daría el día anterior
    const match = startDate ? /^(\d{4})-(\d{2})-(\d{2})/.exec(String(startDate)) : null;
    if (!match) {
      return '-';
    }

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('es-ES', { month: 'short' }).toUpperCase();
    return `${day} ${month} ${date.getFullYear()}`;
  }

  getTotalServiceOrders(): number {
    return this.operationsRows?.length || 0;
  }

  getTotalAmount(): number {
    return this.operationsRows?.reduce((sum, row) => sum + row.amountInOrderCurrency, 0) || 0;
  }

  getAllServiceOrders(): any[] {
    return this.operationsRows;
  }

  getUnitPrice(service: Service): number {
    if (this.isInvoiceOrder) {
      return service.servicePricing?.unitPrice || service.servicePricing?.totalPrice || 0;
    } else {
      return service.servicePricing?.unitCost || service.servicePricing?.totalCost || 0;
    }
  }
}
